angular.module('aprima-provider-api').factory('SettingService',
    ['SettingRepository', 'CurrentUser', 'LocalStorageRepository', 'Bridge', '$q', '_', '$log', 'ServerInfoRepository', 'DeviceSettings', 'debounce', 'ProviderSettingsDtoMapper', 'SettingsService', 'SiteSettingsService',
        function (SettingRepository, CurrentUser, LocalStorageRepository, Bridge, $q, _, $log, ServerInfoRepository, DeviceSettings, debounce, ProviderSettingsDtoMapper, SettingsService, SiteSettingsService) {
            'use strict';

            var saveSettingsPromise = $q.when();

            function serverAllowsSavingSettings() {
                return ServerInfoRepository.isFeatureSupported('SaveSettings');
            }

            function loadSettings() {
                LocalStorageRepository.setDefaultAppointmentsBadgeSettings({
                    hideDischarged: true
                });

                return $q.all([
                    loadPatientDashboardTiles(),
                    loadPatientDashboardConfigurations(),
                    loadProviderSettings(),
                    loadSiteSettings(),
                ]);
            }

            function loadPatientDashboardTiles() {
                return SettingRepository.patientDashboardTiles()
                    .then(function (list) {
                        LocalStorageRepository.removePatientDashboardWidgets();
                        LocalStorageRepository.setPatientDashboardWidgets(list);
                    });
            }

            function loadPatientDashboardConfigurations() {
                return SettingRepository.patientDashboardConfigurations()
                    .then(function (config) {
                        LocalStorageRepository.setPatientDashboardConfigurations(config.phoneConfigs, 1);
                        LocalStorageRepository.setPatientDashboardConfigurations(config.tabletConfigs, 2);
                        LocalStorageRepository.setPatientDashboardConfigurations(config.desktopConfigs, 3);
                        LocalStorageRepository.setPatientDashboardConfigurations(config.allConfigs, 'all');
                    })
                    .catch(function () {
                        $log.error('error getting patient dashboard configurations');
                    });
            }

            function loadProviderSettings() {
                var userId = CurrentUser.get().userId;

                if(serverAllowsSavingSettings()) {
                    return SettingRepository.getProviderSettings(userId)
                        .then(function (providerSettings) {
                            var deviceSettings;

                            if (DeviceSettings.isPhone) {
                                deviceSettings = providerSettings.PhoneSettings;
                            }
                            else if (DeviceSettings.isTablet) {
                                deviceSettings = providerSettings.TabletSettings;
                            }
                            else {
                                deviceSettings = providerSettings.DesktopSettings;
                            }

                            if(!deviceSettings){
                                deviceSettings = providerSettings.PhoneSettings ||
                                    providerSettings.TabletSettings ||
                                    providerSettings.DesktopSettings;
                            }

                            if (deviceSettings) {
                                setDashboardSettings(deviceSettings.DashboardSettings, deviceSettings);
                                setGeneralNoteTypeSettings(deviceSettings.GeneralNoteTypes);
                                LocalStorageRepository.setCurrentUserLicenses(deviceSettings.Licenses);
                                LocalStorageRepository.setIsERxEnabled(deviceSettings.IsERxEnabled);
                                LocalStorageRepository.setAllowImperialUnits(deviceSettings.AllowImperialUnitsInPrescription);
                                var superbillMethod = deviceSettings.DefaultSuperbillStatusId || 0;
                                LocalStorageRepository.setDefaultSuperbillSubmissionMethod(superbillMethod);
                                SettingsService.setProviderDeviceSettings(providerSettings);
                                LocalStorageRepository.setIsCacheClient(deviceSettings.IsCacheClient);
                            }
                        });
                }
                else {
                    var dashboardRequest = SettingRepository.providerDashboard(userId)
                        .then(function (dashboardSettings) {
                            setDashboardSettings(dashboardSettings);
                        });

                    var userSettingsRequest = SettingRepository.userSettings(userId)
                        .then(function (userSettings) {
                            setGeneralNoteTypeSettings(userSettings);
                            LocalStorageRepository.setCurrentUserLicenses(null);
                            LocalStorageRepository.setIsERxEnabled(userSettings.IsERxEnabled || false);
                            LocalStorageRepository.setAllowImperialUnits(userSettings.AllowImperialUnitsInPrescription || false);
                            LocalStorageRepository.setIsCacheClient(userSettings.IsCacheClient || false);
                        });

                    return $q.all([dashboardRequest, userSettingsRequest]);
                }
            }

            function loadSiteSettings() {
                if (serverAllowsSavingSettings()) {
                    return SettingRepository.siteSettings()
                        .then(function (siteSettings) {
                            SiteSettingsService.setSiteSettings(siteSettings);
                        });
                }
            }

            function updateNativeSettings() {
                var settings = [];

                var badgeSetting = {
                    Name: 'BadgeAppointmentsHideDischarged',
                    Value: LocalStorageRepository.getAppointmentsBadgeSettings().hideDischarged
                };

                var calendarSettings = LocalStorageRepository.getAppointmentsListSettings();
                var currentCalendar = calendarSettings &&
                    calendarSettings.calendarIds &&
                    calendarSettings.calendarIds[calendarSettings.index];
                var calendarUidSetting = {
                    Name: 'BadgeAppointmentsCalendarUid',
                    Value: currentCalendar
                };

                var messageListSettings = LocalStorageRepository.getMessagesListSettings();
                var messageFilter = messageListSettings &&
                    messageListSettings.filters &&
                    messageListSettings.filters[messageListSettings.startFilter];
                var messageFilterUidSetting = {
                    Name: 'BadgeMessageFilterUid',
                    Value: messageFilter && messageFilter.Id
                };

                settings.push(badgeSetting);
                settings.push(calendarUidSetting);
                settings.push(messageFilterUidSetting);
                $log.log('badge settings ' + settings);
                Bridge.updateSettings(settings);
            }

            function setDashboardSettings(dashboardSettings, providerSettings) {
                setDashboardTileSettings(dashboardSettings);
                setCalendarSettings(dashboardSettings, providerSettings && providerSettings.ScheduleSettings);
                setMessageSettings(dashboardSettings, providerSettings && providerSettings.MessageSettings);

                updateNativeSettings();
            }

            function setGeneralNoteTypeSettings(generalNoteTypes) {
                LocalStorageRepository.setPhoneMessageGeneralNoteTypeId(generalNoteTypes.PhoneMessageGeneralNoteTypeId || '10101010-0000-0000-0000-000000000003');
                LocalStorageRepository.setTaskMessageGeneralNoteTypeId(generalNoteTypes.TaskMessageGeneralNoteTypeId || '10101010-0000-0000-0000-000000000005');
                LocalStorageRepository.setPortalMessageGeneralNoteTypeId(generalNoteTypes.PortalMessageGeneralNoteTypeId || '5E9280E5-3388-49E8-A5AE-4DFFBB0A9B89');
                LocalStorageRepository.setPatientVitalsDefaultSettingId(generalNoteTypes.DefaultVitalsSettingId || null);
                LocalStorageRepository.setRxRefillMessageGeneralNoteTypeId(generalNoteTypes.RxRefillMessageGeneralNoteTypeId || 'A2B88BC2-E7C8-4990-92F6-AE28B5C0DC77');
                LocalStorageRepository.setPharmacyRefillMessageGeneralNoteTypeId(LocalStorageRepository.getRxRefillMessageGeneralNoteTypeId()); // Reuse for now but store separately
                LocalStorageRepository.setPharmacyGeneralNoteTypeId(generalNoteTypes.PharmacyGeneralNoteTypeId || '10101010-0000-0000-0000-000000000010');
            }

            function setDashboardTileSettings(dashboardSettings) {
                var tiles = dashboardSettings.Tiles;

                _.forEach(tiles, function (tile) {
                    if (tile.Calendars) {
                        tile.Calendar = tile.Calendars.length ? tile.Calendars[0] : null;
                    }
                    else if(tile.Calendar) {
                        tile.Calendars = [tile.Calendar];
                    }

                    if(tile.ExternalPatientsFilter && tile.ExternalPatientsFilter.ServiceSite && !LocalStorageRepository.getDefaultServiceSiteId()) {
                        LocalStorageRepository.setDefaultServiceSiteId(tile.ExternalPatientsFilter.ServiceSite.Id);
                    }
                });

                LocalStorageRepository.setDefaultDashboardTileSettings({
                    Tiles: tiles
                });
            }

            function setCalendarSettings(dashboardSettings, scheduleSettings) {
                var setting;

                if(scheduleSettings){
                    setting = {
                        calendarIds: scheduleSettings.ScheduleCalendarIds && scheduleSettings.ScheduleCalendarIds.length ? scheduleSettings.ScheduleCalendarIds : [],
                        displayEndTime: !!scheduleSettings.DisplayEndTime,
                        findSlotForNewAppt: !!scheduleSettings.FindSlotForNewAppt,
                        index: 0
                    };
                }
                else {
                    var calendarTiles = _.filter(dashboardSettings.Tiles, {Type: 'Calendar'});
                    var firstCalendarTile = calendarTiles && calendarTiles.length ? calendarTiles[0] : null;

                    setting = {
                        calendarIds: firstCalendarTile && firstCalendarTile.Calendars.length ? _.map(firstCalendarTile.Calendars, 'Id') : [],
                        displayEndTime: false,
                        findSlotForNewAppt: false,
                        index: 0
                    };
                }

                LocalStorageRepository.setDefaultAppointmentsListSettings(setting);
            }

            function setMessageSettings(dashboardSettings, messageSettings) {
                var messageFilters;

                if(messageSettings) {
                    messageFilters = _.map(messageSettings.MessageFilterIds, function(id) {
                        return {
                            Id: id
                        };
                    });
                }

                if(!messageFilters || !messageFilters.length) {
                    messageFilters = _(dashboardSettings.Tiles)
                        .filter(function(value){
                            return value.Type === 'Messages' && value.MessageFilter;
                        })
                        .map(function(value){
                            return value.MessageFilter;
                        });
                }

                LocalStorageRepository.setDefaultMessagesListSettings({
                    filters:  messageFilters,
                    startFilter: 0
                });
            }

            function updatePatientDashboardConfiguration(dashboardConfiguration){
                if(!dashboardConfiguration.isNewDashboard || dashboardConfiguration.isNewDashboard === false){
                    return SettingRepository.updatePatientDashboardConfiguration(dashboardConfiguration.Id, dashboardConfiguration);
                }
                else{
                    return SettingRepository.createPatientDashboardConfiguration(dashboardConfiguration);
                }
            }

            function deletePatientDashboardConfiguration(dashboardConfiguration){
                if(!dashboardConfiguration.isNewDashboard || dashboardConfiguration.isNewDashboard === false){
                    return SettingRepository.deletePatientDashboardConfiguration(dashboardConfiguration.Id);
                }
                else{
                    var deferred = $q.defer();
                    deferred.resolve({});
                    return deferred.promise;
                }
            }

            function saveSettings() {
                var userId = CurrentUser.get().userId;

                var settings = ProviderSettingsDtoMapper.getProviderSettingsDto();
                return SettingRepository.saveProviderSettings(userId, settings);
            }

            var debouncedSaveSettings = debounce(10000, function debouncedSaveSettings() {
                //prevent from sending multiple requests simultaneously.
                saveSettingsPromise = saveSettingsPromise.finally(function() {
                    return saveSettings();
                });
            });

            function setSetting(setFn, settings){
                var result = _.bind(setFn, LocalStorageRepository)(settings);

                if(serverAllowsSavingSettings()) {
                    debouncedSaveSettings();
                }

                return result;
            }
  
            function getSigWriterSettings() {
                if (!ServerInfoRepository.isFeatureSupported('Sigwriter_ListSettings')) {
                    // if the endpoint isn't available, then return a default value.
                    // This data structure should match the structure of the object returned from the endpoint below.
                    return $q.when({
                        FrequencyDisplayAbbreviationFirst: false,
                        IsDxInPharmacyInstructionsAnError: true,
                    });
                }

                /*
                    getSigWriterSettings() is intended to be called from SigwriterRxService.loadOptions().
                    And, by design, meant to call the respective end point (SettingRepository.getSigWriterSettings)
                    to retrieve the Sig Writer List Settings each time the Sig Writer is opened (called by loadOptions).
                    By doing this, mobile users will not be required to reopen the mobile application to in order for 
                    any behavior to be updates.

                    If, when, the PRM applications become completely web based (not hybrid), this caching 
                    implementation will need to be revisited.
                */
                var deferred = $q.defer();
                SettingRepository.getSigWriterSettings()
                    .then(function(settings) {
                        var sigWriterSettings = {
                            FrequencyDisplayAbbreviationFirst: settings.FrequencyDisplayAbbreviationFirst,
                            IsDxInPharmacyInstructionsAnError: settings.IsDxInPharmacyInstructionsAnError,
                        };
                        deferred.resolve(sigWriterSettings);
                    });
                return deferred.promise;
            }

            return {
                imperialUnitsAllowed: LocalStorageRepository.getAllowImperialUnits.bind(LocalStorageRepository),
                loadSettings: loadSettings,
                updateNativeSettings : updateNativeSettings,
                updatePatientDashboardConfiguration: updatePatientDashboardConfiguration,
                deletePatientDashboardConfiguration: deletePatientDashboardConfiguration,

                getPatientDashboardWidgets: function() {
                    return LocalStorageRepository.getPatientDashboardWidgets();
                },

                getAppointmentsListSettings: function () {
                    return LocalStorageRepository.getAppointmentsListSettings();
                },
                setAppointmentsListSettings: _.partial(setSetting, LocalStorageRepository.setAppointmentsListSettings),

                getMessagesListSettings: function () {
                    return LocalStorageRepository.getMessagesListSettings();
                },
                setMessagesListSettings: _.partial(setSetting, LocalStorageRepository.setMessagesListSettings),

                getAppointmentsBadgeSettings: function () {
                    return LocalStorageRepository.getAppointmentsBadgeSettings();
                },
                setAppointmentsBadgeSettings: _.partial(setSetting, LocalStorageRepository.setAppointmentsBadgeSettings),

                getDashboardTileSettings: function () {
                    return LocalStorageRepository.getDashboardTileSettings();
                },

                setDashboardTileSettings: _.partial(setSetting, LocalStorageRepository.setDashboardTileSettings),

                getSigWriterSettings: getSigWriterSettings
            };
        }
    ])
;
